import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Layers, Plus, Edit, Trash2, Search, RefreshCw, 
  AlertCircle, X, Check, XCircle, TestTube2
} from 'lucide-react';
import { categoriesAPI } from '../../services/api';

const TestCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    sort_order: 1,
    description: '',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await categoriesAPI.getAll();
      const data = response.data?.data || response.data || [];
      const cats = Array.isArray(data) ? data : [];
      setCategories(cats);
      setStats({
        total: cats.length,
        active: cats.filter(c => c.active).length,
        inactive: cats.filter(c => !c.active).length
      });
    } catch (err) {
      setError('حدث خطأ في تحميل البيانات');
      setCategories([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const resetForm = () => {
    setFormData({ name_en: '', name_ar: '', sort_order: categories.length + 1, description: '', active: true });
  };

  const handleAdd = () => {
    resetForm();
    setFormData(prev => ({ ...prev, sort_order: categories.length + 1 }));
    setShowAddModal(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      name_en: category.name_en || '',
      name_ar: category.name_ar || '',
      sort_order: category.sort_order || 1,
      description: category.description || '',
      active: category.active !== false
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!formData.name_en.trim()) {
      setError('اسم الفئة مطلوب');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (showEditModal && selectedCategory) {
        await categoriesAPI.update(selectedCategory._id, formData);
      } else {
        await categoriesAPI.create(formData);
      }
      fetchCategories();
      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;
    setDeleting(true);
    try {
      await categoriesAPI.delete(selectedCategory._id);
      setCategories(prev => prev.filter(c => c._id !== selectedCategory._id));
      setShowDeleteModal(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (err) {
      setError('حدث خطأ أثناء الحذف');
    }
    setDeleting(false);
  };

  const filteredCategories = categories.filter(c => 
    c.name_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.name_ar?.includes(searchQuery)
  ).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const inputClasses = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm">إجمالي الفئات</p>
            <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center">
            <Layers className="w-6 h-6 text-white" />
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm">الفئات النشطة</p>
            <p className="text-3xl font-bold text-gray-800">{stats.active}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center">
            <Check className="w-6 h-6 text-white" />
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm">الفئات الغير نشطة</p>
            <p className="text-3xl font-bold text-gray-800">{stats.inactive}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center">
            <XCircle className="w-6 h-6 text-white" />
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm">إجمالي التحاليل</p>
            <p className="text-3xl font-bold text-gray-800">0</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center">
            <TestTube2 className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Layers className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-800">فئات التحاليل</h1>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAdd}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>إضافة فئة جديدة</span>
          </motion.button>
        </div>

        {/* Search */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="البحث عن فئة..."
              className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
            />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
          <button onClick={fetchCategories} className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-200">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="mr-auto"><X className="w-4 h-4" /></button>
        </motion.div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Layers className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">لا توجد فئات</p>
            <button onClick={handleAdd} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl">إضافة فئة</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الترتيب</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الاسم</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">عدد التحاليل</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((category, index) => (
                  <motion.tr
                    key={category._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 group"
                  >
                    <td className="px-6 py-5">
                      <span className="font-mono font-bold text-slate-900 text-sm">{category.sort_order || index + 1}</span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 text-sm truncate">{category.name_en}</p>
                        {category.name_ar && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{category.name_ar}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide border ${
                        category.active 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${category.active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        {category.active ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium border border-blue-200">
                        <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">0</span>
                        تحليل
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEdit(category)}
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => { setSelectedCategory(category); setShowDeleteModal(true); }}
                          className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-150"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  {showEditModal ? 'تعديل الفئة' : 'إضافة فئة جديدة'}
                </h3>
                <button onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="text-slate-400 hover:text-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم الفئة (بالإنجليزية) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name_en}
                    onChange={e => setFormData({...formData, name_en: e.target.value})}
                    className={inputClasses}
                    placeholder="Category Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم الفئة (بالعربية)
                  </label>
                  <input
                    type="text"
                    value={formData.name_ar}
                    onChange={e => setFormData({...formData, name_ar: e.target.value})}
                    className={inputClasses}
                    placeholder="اسم الفئة"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ترتيب العرض <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={e => setFormData({...formData, sort_order: parseInt(e.target.value) || 1})}
                    className={inputClasses}
                    min="1"
                  />
                  <p className="text-xs text-gray-400 mt-1">سيتم عرض الفئات بالترتيب التصاعدي</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الوصف</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    className={inputClasses}
                    rows={3}
                    placeholder="وصف اختياري للفئة"
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={e => setFormData({...formData, active: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-sm text-gray-700">نشط (Active)</span>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحفظ...</>
                  ) : (
                    showEditModal ? 'حفظ التغييرات' : 'إضافة الفئة'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">حذف الفئة</h3>
                <p className="text-gray-500 mb-6">
                  هل أنت متأكد من حذف الفئة "{selectedCategory?.name_en}"؟
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">إلغاء</button>
                  <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحذف...</> : 'حذف'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default TestCategories;

